import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import isEmpty from "lodash/isEmpty";
import { ApplicationFeature } from "@green0meter/api-client";

import {
  Card,
  CardContent,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@components/base";
import { useUserStore } from "@store/user";
import { useGetCurrentCompanyInfo } from "@api";
import DefaultRecommendation from "@components/main/recommendation/DefaultRecommendation";

const SUPPORTED_RECOMMENDATION_FEATURES: ApplicationFeature[] = [
  ApplicationFeature.CO2,
  ApplicationFeature.CSRD,
  ApplicationFeature.PROCUREMENT_BUYER,
];

const DefaultFeatureRecommendationsCard: FC = () => {
  const { user } = useUserStore();
  const { t } = useTranslation();
  const {
    currentCompanyInfo,
    isLoadingCurrentCompanyInfo,
    isSuccessCurrentCompanyInfo,
  } = useGetCurrentCompanyInfo(user);

  const isLoading =
    isLoadingCurrentCompanyInfo ||
    !isSuccessCurrentCompanyInfo ||
    !currentCompanyInfo;

  const features =
    currentCompanyInfo?.companyFeatureFunctionalities
      ?.flatMap((f) => f?.applicationFeature ?? [])
      .filter((feature) => SUPPORTED_RECOMMENDATION_FEATURES.includes(feature)) ??
    [];

  const isSingle = features.length === 1 || (isLoading && features.length === 1);

  if (!isLoading && isEmpty(features)) {
    return null;
  }

  return (
    <Card>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        p={3}
        pb={0}
      >
        <Typography variant="subtitle1">
          {t("randomRecommendations.title")}
        </Typography>
      </Stack>
      <CardContent>
        <Grid container spacing={2}>
          {isLoading
            ? Array(isSingle ? 1 : 2)
                .fill(null)
                .map((_, idx) => (
                  <Grid size={isSingle ? 12 : 6} key={`skeleton-${idx}`}>
                    <Skeleton variant="rectangular" height={380} />
                  </Grid>
                ))
            : features.map((feature, idx) => (
                <Grid size={isSingle ? 12 : 6} key={idx}>
                  <DefaultRecommendation feature={feature} />
                </Grid>
              ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default DefaultFeatureRecommendationsCard;
